import { z } from "zod";
import { TApiUnitMetadata } from "../../common/metadata";

export enum EnvironmentalSource {
  electricity = "electricity",
  remoteHeating = "remoteHeating",
  remoteCooling = "remoteCooling",
  oil = "oil",
  bioGas = "bioGas",

  water = "water",

  kitchenAndFoodWaste = "kitchenAndFoodWaste",
  sludgeOrganic = "sludgeOrganic",
  vegetableWaste = "vegetableWaste",
  animalByproducts = "animalByproducts",
  parkAndGardenWaste = "parkAndGardenWaste",
  mixedProcessedWood = "mixedProcessedWood",
  brownPaper = "brownPaper",
  shreddedPaper = "shreddedPaper",
  mixedPaperCardboardAndCarton = "mixedPaperCardboardAndCarton",
  mixedGlassPackagingWithMetal = "mixedGlassPackagingWithMetal",
  mixedMetals = "mixedMetals",
  mixedEEWaste = "mixedEEWaste",
  lightlyContaminatedSoil = "lightlyContaminatedSoil",
  mineralWool = "mineralWool",
  foilPlasticPackaging = "foilPlasticPackaging",
  foilPlasticOther = "foilPlasticOther",
  hardPlasticOther = "hardPlasticOther",
  mixedSoftAndHardPlasticPackaging = "mixedSoftAndHardPlasticPackaging",
  oilContaminatedMass = "oilContaminatedMass",
  sprayCans = "sprayCans",
  expandedAndExtrudedPlastic = "expandedAndExtrudedPlastic",
  mixedPlasticMixedFractionsNotPackaging = "mixedPlasticMixedFractionsNotPackaging",
  fluorescentTubesAndEnergySavingBulbs = "fluorescentTubesAndEnergySavingBulbs",
  leadAcidBatteries = "leadAcidBatteries",
  nonRechargeableBatteries = "nonRechargeableBatteries",
  cleaningProducts = "cleaningProducts",
  gasesInPressurizedContainers = "gasesInPressurizedContainers",
  mixedCommercialWaste = "mixedCommercialWaste",
  sortingResidues = "sortingResidues",

  unknown = "unknown",
}

export enum Rollup {
  MIN = "min",
  AVG = "avg",
  MAX = "max",
  SUM = "sum",
}

export enum Resolution {
  YEAR = "year",
  MONTH = "month",
  QUARTER = "quarter",
  WEEK = "week",
  DAY = "day",
  HOUR = "hour",
}

export type EnvironmentalSettings = {
  from: string;
  to: string;
  resolution: Resolution;
  rollup: Rollup;
};

export type IApiEnvironmentalData = z.infer<typeof TApiEnvironmentalData>;
export const TApiEnvironmentalData = z.object({
  value: z.number(),
  sourceValues: z.array(z.number()),
  timestamp: z.union([
    z.string().datetime({ offset: true }),
    z.string().date(),
  ]),
  sortingDegree: z.number().optional(),
});

export type IApiArea = z.infer<typeof TApiArea>;
export const TApiArea = z.object({
  value: z.union([z.number(), z.null()]).optional(),
  suffix: z.string().optional(),
});

export type IApiAreaUnit = z.infer<typeof TApiAreaUnit>;
export const TApiAreaUnit = z.object({
  id: z.string(),
  name: z.string(),
  area: TApiArea,
});

export type IApiEnvironmentalDataCollection = z.infer<
  typeof TApiEnvironmentalDataCollection
>;
export const TApiEnvironmentalDataCollection = z.object({
  unit: z.string(),
  total: z.number(),
  from: z.string().date(),
  to: z.string().date(),
  rollup: z.nativeEnum(Rollup),
  resolution: z.nativeEnum(Resolution),
  sources: z.array(z.nativeEnum(EnvironmentalSource)),
  environmentalData: z.array(TApiEnvironmentalData),
  totalSortingDegree: z.number().optional(),
  integrationName: z.string(),
  timeFetched: z.string().datetime({ offset: true }),
  totalArea: TApiArea,
  areaUnits: z.array(TApiAreaUnit),
});

export type IApiAllEnvironmentalDataCollections = z.infer<
  typeof TApiAllEnvironmentalDataCollections
>;
export const TApiAllEnvironmentalDataCollections = z.object({
  energy: TApiEnvironmentalDataCollection.nullish(),
  water: TApiEnvironmentalDataCollection.nullish(),
  waste: TApiEnvironmentalDataCollection.nullish(),
});

export type IApiEnvironmentalProperty = z.infer<
  typeof TApiEnvironmentalProperty
>;
export const TApiEnvironmentalProperty = z.object({
  propertyID: z.string(),
  propertyName: z.string(),
  energyMark: z.string(),
  environmentalDataCollection: TApiAllEnvironmentalDataCollections,
});

export type IApiEnvironmentInvoice = z.infer<typeof TApiEnvironmentInvoice>;
export const TApiEnvironmentInvoice = z.object({
  id: z.string(),
  unit: TApiUnitMetadata,
  periodFrom: z.string(),
  periodTo: z.string(),
});

export type IApiEnvironmentInvoices = z.infer<typeof TApiEnvironmentInvoices>;
export const TApiEnvironmentInvoices = z.array(TApiEnvironmentInvoice);
